import buildUrl from "build-url";

const DEFAULT_REQUEST_PARAMS = {
  method: "GET",
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
};

function getUrl(path, queryParams = {}, urlOverride = null) {
  const opts = { path };

  if (Object.keys(queryParams).length !== 0) {
    opts.queryParams = queryParams;
  }

  return buildUrl("//" + (urlOverride ?? process.env.HOST_API), opts);
}

async function makeRequest(token, path, params, urlOverride = null) {
  const url = getUrl(path, params?.query, urlOverride);

  // Set token in headers if available
  if (token !== null) {
    params.headers = {
      ...params.headers,
      Authorization: `Bearer ${token}`,
    };
  }

  // Set Site header
  if (location.hostname && !urlOverride) {
    params.headers["X-Site"] = location.hostname;
  }

  // Set captcha header
  if (params.captcha) {
    params.headers["X-Captcha"] = params.captcha;
  }

  // JSONify the body
  if (params.body) {
    params.body = JSON.stringify(params.body);
  }

  return fetch(url, params).then((response) =>
    response.text().then((rawData) => ({
      data: rawData.length === 0 ? null : JSON.parse(rawData),
      response: response,
    }))
  );
}

function sendView(token, type, id, data = null) {
  return makeRequest(token, `${type}/${id}/view`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
    body: data,
  });
}

function fetchCurrentUser(token) {
  return makeRequest(token, "user", { ...DEFAULT_REQUEST_PARAMS });
}

function fetchConfig(token) {
  return makeRequest(token, "config", {
    ...DEFAULT_REQUEST_PARAMS
  });
}

function fetchCounts(token) {
  return makeRequest(token, "user/counts", { ...DEFAULT_REQUEST_PARAMS });
}

function fetchAccessToken(refreshToken) {
  return makeRequest(refreshToken, "user/token", {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
  });
}

function registerAccount(token, captcha, email, locale) {
  return makeRequest(token, "user/register", {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
    captcha: captcha ?? null,
    body: { email, locale },
  });
}

function activateAccount(token, data) {
  return makeRequest(token, "user/activate", {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
    body: data,
  });
}

function requestEmailChange(token, userId, data) {
  return makeRequest(token, `/user/${userId}/change-email`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
    body: data,
  });
}

function verifyEmail(token, data) {
  return makeRequest(token, `/user/verify-email`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
    body: data,
  });
}

function resetPassword(data) {
  return makeRequest(null, "user/reset-password", {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
    body: data,
  });
}

function forgotPassword(data) {
  return makeRequest(null, "user/forgot-password", {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
    body: data,
  });
}

function requestPasswordReset(token, userId) {
  return makeRequest(token, `/user/${userId}/reset-password`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
  });
}

function login(token, email, password) {
  return makeRequest(token, "user/login", {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
    body: { email, password },
  });
}

function createPoll(token, data) {
  return makeRequest(token, "polls", {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
    body: data,
  });
}

function fetchPoll(token, id) {
  return makeRequest(token, `poll/${id}`, { ...DEFAULT_REQUEST_PARAMS });
}

function createPollVote(token, pollId, optionId) {
  return makeRequest(token, `poll/${pollId}/vote/${optionId}`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
  });
}

function createVideo(token, captcha, data) {
  return makeRequest(token, "videos", {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
    body: data,
    captcha: captcha ?? null,
  });
}

function updateVideo(token, id, data) {
  return makeRequest(token, `video/${id}`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "PUT",
    body: data,
  });
}

function destroyVideo(token, id) {
  return makeRequest(token, `video/${id}`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "DELETE",
  });
}

function fetchFriends(token, userId, params = {}) {
  return makeRequest(token, `user/${userId}/friends`, {
    ...DEFAULT_REQUEST_PARAMS,
    query: params,
  });
}

function fetchFriendRequests(token, userId, params = {}) {
  return makeRequest(token, `/user/${userId}/friends/requests`, {
    ...DEFAULT_REQUEST_PARAMS,
    query: params,
  });
}

function fetchFriendStatus(token, userId) {
  return makeRequest(token, `/user/${userId}/friends/status`, {
    ...DEFAULT_REQUEST_PARAMS,
  });
}

function requestFriend(token, userId) {
  return makeRequest(token, `user/${userId}/friends`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
  });
}

function destroyFriend(token, userId) {
  return makeRequest(token, `user/${userId}/friends`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "DELETE",
  });
}

function fetchVideos(token, filters) {
  return makeRequest(token, "videos", {
    ...DEFAULT_REQUEST_PARAMS,
    query: filters,
  });
}

function fetchUserHistory(token, id, type, filters) {
  return makeRequest(token, `/user/${id}/history/${type}`, {
    ...DEFAULT_REQUEST_PARAMS,
    query: filters,
  });
}

function fetchCooldown(token, type) {
  return makeRequest(token, `user/${type}/cooldown`, {
    ...DEFAULT_REQUEST_PARAMS,
  });
}

function fetchWarnings(token, userId, filters) {
  return makeRequest(token, `user/${userId}/warnings`, {
    ...DEFAULT_REQUEST_PARAMS,
    query: filters,
  });
}

function fetchBans(token, userId, filters) {
  return makeRequest(token, `user/${userId}/bans`, {
    ...DEFAULT_REQUEST_PARAMS,
    query: filters,
  });
}

function fetchRules() {
  return makeRequest(null, "rules", {
    ...DEFAULT_REQUEST_PARAMS,
  });
}

function fetchRule(id) {
  return makeRequest(null, `rule/${id}`, {
    ...DEFAULT_REQUEST_PARAMS,
  });
}

function fetchVideo(token, id) {
  return makeRequest(token, `video/${id}`, { ...DEFAULT_REQUEST_PARAMS });
}

function fetchRelated(token, type, id) {
  return makeRequest(token, `${type}/${id}/related`, {
    ...DEFAULT_REQUEST_PARAMS,
  });
}

function createImage(token, captcha, data) {
  return makeRequest(token, "images", {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
    body: data,
    captcha: captcha ?? null,
  });
}

function updateImage(token, id, data) {
  return makeRequest(token, `image/${id}`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "PUT",
    body: data,
  });
}

function destroyImage(token, id) {
  return makeRequest(token, `image/${id}`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "DELETE",
  });
}

function fetchImages(token, filters) {
  return makeRequest(token, "images", {
    ...DEFAULT_REQUEST_PARAMS,
    query: filters,
  });
}

function fetchTrending(token, type, filters) {
  return makeRequest(token, `trending/${type}`, {
    ...DEFAULT_REQUEST_PARAMS,
    query: filters,
  });
}

function fetchImage(token, id) {
  return makeRequest(token, `image/${id}`, { ...DEFAULT_REQUEST_PARAMS });
}

function fetchProducts(token, filters) {
  return makeRequest(token, "products", {
    ...DEFAULT_REQUEST_PARAMS,
    query: filters,
  });
}

function fetchProduct(token, id) {
  return makeRequest(token, `product/${id}`, {
    ...DEFAULT_REQUEST_PARAMS,
  });
}

function purchaseProduct(token, id) {
  return makeRequest(token, `/product/${id}/purchase`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
  });
}

function fetchVouchers(token, filters) {
  return makeRequest(token, "admin/vouchers", {
    ...DEFAULT_REQUEST_PARAMS,
    query: filters,
  });
}

function createPost(token, captcha, data) {
  return makeRequest(token, "posts", {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
    captcha: captcha ?? null,
    body: data,
  });
}

function fetchPosts(token, filters) {
  return makeRequest(token, "posts", {
    ...DEFAULT_REQUEST_PARAMS,
    query: filters,
  });
}

function fetchPost(token, id) {
  return makeRequest(token, `post/${id}`, { ...DEFAULT_REQUEST_PARAMS });
}

function fetchPage(token, slug) {
  return makeRequest(token, `page/${slug}`, { ...DEFAULT_REQUEST_PARAMS });
}

function createPage(token, data) {
  return makeRequest(token, "pages", {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
    body: data,
  });
}

function updatePage(token, slug, data) {
  return makeRequest(token, `page/${slug}`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "PUT",
    body: data,
  });
}

function updatePost(token, id, data) {
  return makeRequest(token, `post/${id}`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "PUT",
    body: data,
  });
}

function destroyPost(token, id) {
  return makeRequest(token, `post/${id}`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "DELETE",
  });
}

function fetchForumSections(token = null) {
  return makeRequest(token, "forum", {
    ...DEFAULT_REQUEST_PARAMS,
  });
}

function fetchForumThreads(token = null, filters = {}) {
  return makeRequest(token, "forum/threads", {
    ...DEFAULT_REQUEST_PARAMS,
    query: filters,
  });
}

function fetchForumSection(token, id, filters) {
  return makeRequest(null, `forum/${id}`, {
    ...DEFAULT_REQUEST_PARAMS,
    query: filters,
  });
}

function fetchForumThread(token, id, section, filters) {
  return makeRequest(token, `forum/${section}/${id}`, {
    ...DEFAULT_REQUEST_PARAMS,
    query: filters,
  });
}

function createForumThread(token, captcha, data) {
  return makeRequest(token, `forum/${data?.section}`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
    captcha: captcha ?? null,
    body: data,
  });
}

function updateForumThread(token, id, data) {
  return makeRequest(token, `forum/${id}`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "PUT",
    body: data,
  });
}

function destroyForumThread(token, id) {
  return makeRequest(token, `forum/${id}`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "DELETE",
  });
}

function createForumReply(token, id, captcha, data) {
  return makeRequest(token, `forum/${id}/reply`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
    captcha: captcha ?? null,
    body: data,
  });
}

function updateForumReply(token, id, data) {
  return makeRequest(token, `forum/post/${id}`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "PUT",
    body: data,
  });
}

function destroyForumReply(token, id) {
  return makeRequest(token, `forum/post/${id}`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "DELETE",
  });
}

function fetchProfile(token, id) {
  return makeRequest(token, `profile/${id}`, {
    ...DEFAULT_REQUEST_PARAMS,
  });
}

function updateUser(token, id, data) {
  return makeRequest(token, `user/${id}`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "PUT",
    body: data,
  });
}

function updateProfile(token, id, data) {
  return makeRequest(token, `profile/${id}`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "PUT",
    body: data,
  });
}

function fetchTags(query = {}) {
  return makeRequest(null, "tags", {
    ...DEFAULT_REQUEST_PARAMS,
    query,
  });
}

function fetchTag(token, id) {
  return makeRequest(token, `tags/${id}`, {
    ...DEFAULT_REQUEST_PARAMS,
  });
}

function suggestTag(token, data) {
  return makeRequest(token, "tags/suggestions", {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
    body: data,
  });
}

function fetchAutocompleteTags(token, query) {
  return makeRequest(token, "autocomplete/tags", {
    ...DEFAULT_REQUEST_PARAMS,
    query: { query },
  });
}

// Params: object containing either "query" or "id"
function fetchAutocompleteUsers(params) {
  return makeRequest(null, "autocomplete/users", {
    ...DEFAULT_REQUEST_PARAMS,
    query: params,
  });
}

function like(token, type, id) {
  return makeRequest(token, `${type}/${id}/like`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
  });
}

function unlike(token, type, id) {
  return makeRequest(token, `${type}/${id}/like`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "DELETE",
  });
}

function fetchLikes(type, id, filters = {}) {
  return makeRequest(null, `${type}/${id}/likes`, {
    ...DEFAULT_REQUEST_PARAMS,
    query: filters,
  });
}

function fetchFavorites(token, type, filters) {
  return makeRequest(token, `favorites/${type}`, {
    ...DEFAULT_REQUEST_PARAMS,
    query: filters,
  });
}

function fetchComments(token, type, id, page = 0, filters = {}) {
  return makeRequest(token, `${type}/${id}/comments`, {
    ...DEFAULT_REQUEST_PARAMS,
    query: { ...filters, page },
  });
}

function createComment(token, type, id, data) {
  return makeRequest(token, `${type}/${id}/comments`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
    body: data,
  });
}

function updateComment(token, id, data) {
  return makeRequest(token, `comment/${id}`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "PUT",
    body: data,
  });
}

function destroyComment(token, id) {
  return makeRequest(token, `comment/${id}`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "DELETE",
  });
}

function fetchPlaylist(token, id, filters = {}) {
  return makeRequest(token, `playlist/${id}`, {
    ...DEFAULT_REQUEST_PARAMS,
    query: filters,
  });
}

function fetchPlaylists(token, filters = {}) {
  return makeRequest(token, "playlists", {
    ...DEFAULT_REQUEST_PARAMS,
    query: filters,
  });
}

function createPlaylist(token, data) {
  return makeRequest(token, "playlists", {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
    body: data,
  });
}

function updatePlaylist(token, id, data) {
  return makeRequest(token, `playlist/${id}`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "PUT",
    body: data,
  });
}

function destroyPlaylist(token, id) {
  return makeRequest(token, `playlist/${id}`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "DELETE",
  });
}

function addToPlaylist(token, playlistId, videoId) {
  return makeRequest(token, `playlist/${playlistId}/${videoId}`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
  });
}

function removeFromPlaylist(token, playlistId, videoId) {
  return makeRequest(token, `playlist/${playlistId}/${videoId}`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "DELETE",
  });
}

function fetchNotifications(token, id) {
  return makeRequest(token, `user/${id}/notifications`, {
    ...DEFAULT_REQUEST_PARAMS,
  });
}

function setNotificationRead(token, id) {
  return makeRequest(token, `notifications/${id}/read`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
  });
}

function fetchConversations(token, id, params = {}) {
  return makeRequest(token, `user/${id}/conversations`, {
    ...DEFAULT_REQUEST_PARAMS,
    query: params,
  });
}

function createConversation(token, userId, data) {
  return makeRequest(token, `user/${userId}/conversations`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
    body: data,
  });
}

function createMessage(token, conversationId, data) {
  return makeRequest(token, `conversation/${conversationId}/messages`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
    body: data,
  });
}

function destroyMessage(token, messageId) {
  return makeRequest(token, `message/${messageId}`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "DELETE",
  });
}

function fetchMessages(token, conversationId, params = {}) {
  return makeRequest(token, `conversation/${conversationId}/messages`, {
    ...DEFAULT_REQUEST_PARAMS,
    query: params,
  });
}

function fetchLightContent(token, type, id = null, query = {}) {
  let url = `light/${type}`;

  if (id !== null) {
    url += `/${id}`;
  }

  return makeRequest(token, url, {
    ...DEFAULT_REQUEST_PARAMS,
    query,
  });
}

function fetchLovenseCToken(token, userId) {
  return makeRequest(token, "partners/lovense/ctoken", {
    ...DEFAULT_REQUEST_PARAMS,
    query: { userId },
  });
}

function fetchFollowers(userId, filters) {
  return makeRequest(null, `user/${userId}/followers`, {
    ...DEFAULT_REQUEST_PARAMS,
    query: filters,
  });
}

function fetchFollowing(userId, filters) {
  return makeRequest(null, `user/${userId}/following`, {
    ...DEFAULT_REQUEST_PARAMS,
    query: filters,
  });
}

function fetchUserContent(token, userId, type, filters = {}) {
  return makeRequest(token, `/user/${userId}/content/${type}`, {
    ...DEFAULT_REQUEST_PARAMS,
    query: filters,
  });
}

function blockUser(token, userId) {
  return makeRequest(token, `/user/${userId}/block`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
  });
}

function fetchUserBlockStatus(token, userId) {
  return makeRequest(token, `/user/${userId}/block`, {
    ...DEFAULT_REQUEST_PARAMS,
  });
}

function unblockUser(token, userId) {
  return makeRequest(token, `/user/${userId}/block`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "DELETE",
  });
}

function fetchBlockedUsers(token) {
  return makeRequest(token, "/user/blocked", {
    ...DEFAULT_REQUEST_PARAMS,
  });
}

function createFollow(token, userId) {
  return makeRequest(token, `/user/${userId}/followers`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
  });
}

function destroyFollow(token, userId) {
  return makeRequest(token, `/user/${userId}/followers`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "DELETE",
  });
}

function fetchSearchResults(token, filters) {
  return makeRequest(token, "search", {
    ...DEFAULT_REQUEST_PARAMS,
    query: filters,
  });
}

function fetchMigrationReference(type, id) {
  return makeRequest(null, `/migration/lookup/${type}/${id}`, {
    ...DEFAULT_REQUEST_PARAMS,
  });
}

function fetchCaptcha(token) {
  return makeRequest(token, "captcha", {
    ...DEFAULT_REQUEST_PARAMS,
  });
}

function createDonation(token, userId, amount, message, source = {}) {
  return makeRequest(token, `/user/${userId}/donate`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
    body: { amount, message, source },
  })
}

function fetchDonations(token, filters = {}) {
  return makeRequest(token, "/user/donations", {
    ...DEFAULT_REQUEST_PARAMS,
    query: filters,
  });
}

function fetchDashboard(token, filters = {}) {
  return makeRequest(token, "/user/dashboard", {
    ...DEFAULT_REQUEST_PARAMS,
    query: filters,
  });
}

function fetchUserCreatorProgramStatus(token, userId) {
  return makeRequest(token, `/user/${userId}/creator-program`, {
    ...DEFAULT_REQUEST_PARAMS,
  });
}

function joinUserCreatorProgram(token, userId) {
  return makeRequest(token, `/user/${userId}/creator-program`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
  });
}

function leaveUserCreatorProgram(token, userId) {
  return makeRequest(token, `/user/${userId}/creator-program`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "DELETE",
  });
}

function fetchTransactions(token, filters = {}) {
  return makeRequest(token, "/user/transactions", {
    ...DEFAULT_REQUEST_PARAMS,
    query: filters,
  });
}

function fetchAdminTagSuggestions(token, id) {
  return makeRequest(token, "tags/suggestions", {
    ...DEFAULT_REQUEST_PARAMS,
  });
}

function fetchAdminTagsList(token, filters = {}) {
  return makeRequest(token, "tags", {
    ...DEFAULT_REQUEST_PARAMS,
    query: filters,
  });
}

function adminUpdateTag(token, id, data) {
  return makeRequest(token, `tags/${id}`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "PUT",
    body: data,
  });
}

function adminCreateTag(token, data) {
  return makeRequest(token, `tags`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
    body: data,
  });
}

function adminDestroyTag(token, id) {
  return makeRequest(token, `tags/${id}`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "DELETE",
  });
}

function adminMigrateTags(token, sourceId, targetId) {
  return makeRequest(token, `tags/migrate`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
    body: { sourceId, targetId },
  });
}

function approveAdminTagSuggestion(token, name) {
  return makeRequest(token, `tags/suggestions/${name}`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
  });
}

function rejectAdminTagSuggestion(token, name) {
  return makeRequest(token, `tags/suggestions/${name}`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "DELETE",
  });
}

function fetchAdminServiceStatus(token) {
  return makeRequest(token, "admin/services", {
    ...DEFAULT_REQUEST_PARAMS,
  });
}

function fetchAdminReviewList(token, params = {}) {
  return makeRequest(token, "admin/reviews", {
    ...DEFAULT_REQUEST_PARAMS,
    query: params,
  });
}

function adminApproveReview(token, reviewId) {
  return makeRequest(token, `admin/review/${reviewId}`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
  });
}

function adminRejectReview(token, reviewId) {
  return makeRequest(token, `admin/review/${reviewId}`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "DELETE",
  });
}

function fetchAdminUserList(token, params = {}) {
  return makeRequest(token, "admin/users", {
    ...DEFAULT_REQUEST_PARAMS,
    query: params,
  });
}

function fetchAdminUserDetail(token, id) {
  return makeRequest(token, `admin/user/${id}`, {
    ...DEFAULT_REQUEST_PARAMS,
  });
}

function fetchAdminLogs(token, params = {}) {
  return makeRequest(token, `admin/logs`, {
    ...DEFAULT_REQUEST_PARAMS,
    query: params,
  });
}

function fetchAdminDashboard(token) {
  return makeRequest(token, `admin/dashboard`, {
    ...DEFAULT_REQUEST_PARAMS,
  });
}

function adminIssueRefund(token, type, id) {
  return makeRequest(token, `admin/ledger/refund/${type}/${id}`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
  });
}

function adminFetchLedgerStats(token, params = {}) {
  return makeRequest(token, "admin/ledger/stats", {
    ...DEFAULT_REQUEST_PARAMS,
    query: params,
  });
}

function adminFetchTransactions(token, params = {}) {
  return makeRequest(token, "admin/ledger/transactions", {
    ...DEFAULT_REQUEST_PARAMS,
    query: params,
  });
}

function adminFetchTransaction(token, id) {
  return makeRequest(token, `admin/ledger/transactions/${id}`, {
    ...DEFAULT_REQUEST_PARAMS,
  });
}

function adminFetchPurchases(token, params = {}) {
  return makeRequest(token, "admin/ledger/purchases", {
    ...DEFAULT_REQUEST_PARAMS,
    query: params,
  });
}

function adminFetchPurchase(token, id) {
  return makeRequest(token, `admin/ledger/purchases/${id}`, {
    ...DEFAULT_REQUEST_PARAMS,
  });
}

function adminFetchDonations(token, params = {}) {
  return makeRequest(token, `admin/ledger/donations`, {
    ...DEFAULT_REQUEST_PARAMS,
    query: params,
  });
}

function adminFetchDonation(token, id) {
  return makeRequest(token, `admin/ledger/donations/${id}`, {
    ...DEFAULT_REQUEST_PARAMS,
  });
}

function adminActionReIndex(token) {
  return makeRequest(token, `admin/actions/reindex`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
  });
}

function adminActionSearchRecreate(token, collectionId) {
  return makeRequest(token, `/admin/actions/search-recreate/${collectionId}`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
  });
}

function actionSearchReindex(token) {
  return makeRequest(token, `admin/actions/search-reindex`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
  });
}

function adminActionSyncVideos(token) {
  return makeRequest(token, `admin/actions/sync-videos`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
  });
}

function adminBatch(token, jobId, data = {}) {
  return makeRequest(token, `admin/batch/${jobId}`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
    body: data,
  });
}

function adminGenerateVouchers(token, data) {
  return makeRequest(token, `/admin/vouchers`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
    body: data,
  });
}

function adminCreateWarning(token, userId, data) {
  return makeRequest(token, `user/${userId}/warnings`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
    body: data,
  });
}

function adminCreateRule(token, data) {
  return makeRequest(token, "rules", {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
    body: data,
  });
}

function adminUpdateRule(token, id, data) {
  return makeRequest(token, `rule/${id}`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "PUT",
    body: data,
  });
}

function adminDestroyRule(token, id) {
  return makeRequest(token, `rule/${id}`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "DELETE",
  });
}

function adminPurgeUser(token, id) {
  return makeRequest(token, `admin/user/${id}`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "DELETE",
  });
}

function adminSpamPurge(token, id, data) {
  return makeRequest(token, `admin/user/${id}/spam`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "DELETE",
    body: data,
  });
}

function destroyUser(token, id) {
  return makeRequest(token, `/user/${id}`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "DELETE",
  });
}

function adminBanUser(token, id, data) {
  return makeRequest(token, `user/${id}/bans`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
    body: data,
  });
}

function adminUnbanUser(token, id) {
  return makeRequest(token, `user/${id}/bans`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "DELETE",
  });
}

function fetchTwitterAuthUrl() {
  return makeRequest(null, "twitter/authenticate", {
    ...DEFAULT_REQUEST_PARAMS,
  });
}

function fetchTwitterAuthCallback(data) {
  return makeRequest(null, "twitter/callback", {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
    body: data,
  });
}

function adminCreateProduct(token, data) {
  return makeRequest(token, "products", {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
    body: data,
  });
}

function adminUpdateProduct(token, id, data) {
  return makeRequest(token, `product/${id}`, {
    ...DEFAULT_REQUEST_PARAMS,
    method: "PUT",
    body: data,
  });
}

function adminUpsertZones(token, data) {
  return makeRequest(token, "admin/config/zones", {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
    body: data,
  });
}

function adminUpsertAnnouncement(token, data) {
  return makeRequest(token, "admin/config/announcement", {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
    body: data,
  });
}

function adminUpsertDefcon(token, data) {
  return makeRequest(token, "admin/config/defcon", {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
    body: data,
  });
}

function adminFetchDefcon(token) {
  return makeRequest(token, "admin/config/defcon", {
    ...DEFAULT_REQUEST_PARAMS,
  });
}

function adminFetchFiles(token, filters = {}) {
  return makeRequest(
    token,
    "files",
    {
      ...DEFAULT_REQUEST_PARAMS,
      query: filters,
    },
    process.env.HOST_FILES
  );
}

function adminFetchQueues(token, service) {
  const host =
    service === "api" ? process.env.HOST_API : process.env.HOST_FILES;
  return makeRequest(
    token,
    "/admin/queues",
    {
      ...DEFAULT_REQUEST_PARAMS,
    },
    host
  );
}

function adminFetchQueue(token, service, queueId) {
  const host =
    service === "api" ? process.env.HOST_API : process.env.HOST_FILES;
  return makeRequest(
    token,
    `/admin/queues/${queueId}`,
    {
      ...DEFAULT_REQUEST_PARAMS,
    },
    host
  );
}

function adminDrainQueue(token, service, queueId) {
  const host =
    service === "api" ? process.env.HOST_API : process.env.HOST_FILES;
  return makeRequest(
    token,
    `/admin/queues/${queueId}/drain`,
    {
      ...DEFAULT_REQUEST_PARAMS,
      method: "POST",
    },
    host
  );
}

function adminPauseQueue(token, service, queueId) {
  const host =
    service === "api" ? process.env.HOST_API : process.env.HOST_FILES;
  return makeRequest(
    token,
    `/admin/queues/${queueId}/pause`,
    {
      ...DEFAULT_REQUEST_PARAMS,
      method: "POST",
    },
    host
  );
}

function adminResumeQueue(token, service, queueId) {
  const host =
    service === "api" ? process.env.HOST_API : process.env.HOST_FILES;
  return makeRequest(
    token,
    `/admin/queues/${queueId}/resume`,
    {
      ...DEFAULT_REQUEST_PARAMS,
      method: "POST",
    },
    host
  );
}

function adminFetchJob(token, service, queueId, jobId) {
  const host =
    service === "api" ? process.env.HOST_API : process.env.HOST_FILES;
  return makeRequest(
    token,
    `/admin/queues/${queueId}/${jobId}`,
    {
      ...DEFAULT_REQUEST_PARAMS,
    },
    host
  );
}

function adminRemoveJob(token, service, queueId, jobId) {
  const host =
    service === "api" ? process.env.HOST_API : process.env.HOST_FILES;
  return makeRequest(
    token,
    `/admin/queues/${queueId}/${jobId}`,
    {
      ...DEFAULT_REQUEST_PARAMS,
      method: "DELETE",
    },
    host
  );
}

function adminFetchPages(token, filters = {}) {
  return makeRequest(token, "pages", {
    ...DEFAULT_REQUEST_PARAMS,
    query: filters,
  });
}

function adminFetchSearch(token) {
  return makeRequest(token, "admin/search", {
    ...DEFAULT_REQUEST_PARAMS,
  });
}

function redeemVoucher(token, data) {
  return makeRequest(token, "user/vouchers", {
    ...DEFAULT_REQUEST_PARAMS,
    method: "POST",
    body: data,
  });
}

function processFileProgress(id, key) {
  return makeRequest(null, `/upload/video/${id}/${key}`, { ...DEFAULT_REQUEST_PARAMS }, process.env.HOST_FILES);
}

function uploadFile(token, path, file, onProgress = null) {
  const formData = new FormData();
  formData.append("file", file);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("post", `//${process.env.HOST_FILES}${path}`);
    xhr.setRequestHeader("Authorization", `Bearer ${token}`);

    xhr.onload = (e) => {
      resolve({
        statusCode: e.target.status,
        success: e.target.status === 201,
        result: JSON.parse(e.target.responseText),
      });
    };

    xhr.onerror = (err) => {
      reject(err);
    };

    if (xhr.upload && onProgress) {
      xhr.upload.onprogress = (e) =>
        onProgress(Math.round((e.loaded / e.total) * 100));
    }

    xhr.send(formData);
  });
}

const API = {
  createDonation,
  fetchDonations,
  fetchDashboard,
  fetchUserCreatorProgramStatus,
  joinUserCreatorProgram,
  leaveUserCreatorProgram,
  fetchTransactions,
  activateAccount,
  addToPlaylist,
  blockUser,
  createComment,
  createConversation,
  createFollow,
  createForumReply,
  createForumThread,
  createImage,
  createMessage,
  createPage,
  createPlaylist,
  createPoll,
  createPollVote,
  createPost,
  createVideo,
  destroyUser,
  destroyComment,
  destroyFollow,
  destroyForumThread,
  destroyForumReply,
  destroyFriend,
  destroyImage,
  destroyMessage,
  destroyPost,
  destroyVideo,
  fetchAccessToken,
  fetchAutocompleteTags,
  fetchAutocompleteUsers,
  fetchBans,
  fetchBlockedUsers,
  fetchCaptcha,
  fetchComments,
  fetchConfig,
  fetchConversations,
  fetchCooldown,
  fetchCounts,
  fetchCurrentUser,
  fetchFavorites,
  fetchFollowers,
  fetchFollowing,
  fetchForumSection,
  fetchForumSections,
  fetchForumThread,
  fetchForumThreads,
  fetchFriendRequests,
  fetchFriends,
  fetchFriendStatus,
  fetchImage,
  fetchImages,
  fetchLightContent,
  fetchLovenseCToken,
  fetchLikes,
  fetchMessages,
  fetchMigrationReference,
  fetchNotifications,
  fetchPage,
  fetchPlaylist,
  fetchPlaylists,
  fetchPoll,
  fetchPost,
  fetchPosts,
  fetchProduct,
  purchaseProduct,
  fetchProducts,
  fetchProfile,
  fetchRelated,
  fetchRule,
  fetchRules,
  fetchSearchResults,
  fetchTags,
  fetchTag,
  fetchTrending,
  fetchTwitterAuthCallback,
  fetchTwitterAuthUrl,
  fetchUserBlockStatus,
  fetchUserContent,
  fetchUserHistory,
  fetchVideo,
  fetchVideos,
  fetchVouchers,
  fetchWarnings,
  forgotPassword,
  like,
  login,
  redeemVoucher,
  registerAccount,
  removeFromPlaylist,
  requestEmailChange,
  requestFriend,
  requestPasswordReset,
  resetPassword,
  sendView,
  setNotificationRead,
  suggestTag,
  unblockUser,
  unlike,
  updateComment,
  updateForumReply,
  updateForumThread,
  updateImage,
  updatePage,
  updatePlaylist,
  destroyPlaylist,
  updatePost,
  updateProfile,
  updateUser,
  updateVideo,
  uploadFile,
  processFileProgress,
  verifyEmail,

  admin: {
    actionReIndex: adminActionReIndex,
    searchRecreate: adminActionSearchRecreate,
    actionSyncVideos: adminActionSyncVideos,
    approveTag: approveAdminTagSuggestion,
    banUser: adminBanUser,
    purgeUser: adminPurgeUser,
    spamPurge: adminSpamPurge,
    batch: adminBatch,
    issueRefund: adminIssueRefund,
    fetchLedgerStats: adminFetchLedgerStats,
    fetchTransactions: adminFetchTransactions,
    fetchTransaction: adminFetchTransaction,
    fetchPurchases: adminFetchPurchases,
    fetchPurchase: adminFetchPurchase,
    fetchDonations: adminFetchDonations,
    fetchDonation: adminFetchDonation,
    createProduct: adminCreateProduct,
    createWarning: adminCreateWarning,
    fetchDashboard: fetchAdminDashboard,
    fetchFiles: adminFetchFiles,
    fetchJob: adminFetchJob,
    removeJob: adminRemoveJob,
    fetchLogs: fetchAdminLogs,
    fetchPages: adminFetchPages,
    fetchSearch: adminFetchSearch,
    fetchQueue: adminFetchQueue,
    drainQueue: adminDrainQueue,
    pauseQueue: adminPauseQueue,
    resumeQueue: adminResumeQueue,
    fetchQueues: adminFetchQueues,
    fetchServiceStatus: fetchAdminServiceStatus,
    fetchTags: fetchAdminTagSuggestions,
    fetchTagsList: fetchAdminTagsList,
    migrateTags: adminMigrateTags,
    updateTag: adminUpdateTag,
    createTag: adminCreateTag,
    destroyTag: adminDestroyTag,
    fetchUserDetail: fetchAdminUserDetail,
    fetchUserList: fetchAdminUserList,
    fetchReviewList: fetchAdminReviewList,
    approveReview: adminApproveReview,
    rejectReview: adminRejectReview,
    generateVouchers: adminGenerateVouchers,
    rejectTag: rejectAdminTagSuggestion,
    unbanUser: adminUnbanUser,
    updateProduct: adminUpdateProduct,
    upsertZones: adminUpsertZones,
    upsertAnnouncement: adminUpsertAnnouncement,
    upsertDefcon: adminUpsertDefcon,
    fetchDefcon: adminFetchDefcon,
    createRule: adminCreateRule,
    updateRule: adminUpdateRule,
    destroyRule: adminDestroyRule,
  },
};

export default API;
